import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import styles from "./Table.module.scss";
import "./index.css";
import previewImage from "../../Assets/images/piLogo.png";

import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { db } from "../../firebase";
import { useAuth } from "../../Context/AuthContext";
import { Toast } from "primereact/toast";
import { FaSearch } from "react-icons/fa";
import { Image, Row, Col, Modal } from "react-bootstrap";
import { Button } from "primereact/button";
import Header from "../header/header";
import moment from "moment";
import { MultiSelect } from "primereact/multiselect";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import FiltersComponent from "./filter";
import { ProgressSpinner } from 'primereact/progressspinner';
import { debounce } from 'lodash';
import { Pagination } from 'antd';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { buildPlayerRoleSummary } from "../../utils/roleSummaryService";
import { isValidEmail, formatWeightDisplay } from "../../utils/helpers";
import { formatProfileAge, getProfileAgeNumber } from "../../utils/age";
import { useLocation } from "react-router-dom";
import { feetInchesToInchesSafe, formatFeetInches } from "../../utils/feetInches";

// Height conversion functions
const feetInchesToInches = (height) => {
  return feetInchesToInchesSafe(height);
};

const DEFAULT_HEIGHT = [54, 96];   // 4'6" to 8'0" (inches) - matches slider range
const DEFAULT_WEIGHT = [0, 500];   // 0 to 500 lbs
const DEFAULT_WINGSPAN = [0, 120]; // 0 to 10 feet

// Custom hook to get window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Sorting functions - optimized for performance
const sortTeams = (isReverse) => {
  if (isReverse) {
    return function (a, b) {
      return (b.Team || '').localeCompare(a.Team || '');
    };
  }
  return function (a, b) {
    return (a.Team || '').localeCompare(b.Team || '');
  };
};

const sortNames = (isReverse, sortByLastName) => {
  if (isReverse) {
    return function (a, b) {
      const nameA = sortByLastName
        ? (b.LastName || '').toLowerCase()
        : (b.FirstName || '').toLowerCase();
      const nameB = sortByLastName
        ? (a.LastName || '').toLowerCase()
        : (a.FirstName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    };
  }
  return function (a, b) {
    const nameA = sortByLastName
      ? (a.LastName || '').toLowerCase()
      : (a.FirstName || '').toLowerCase();
    const nameB = sortByLastName
      ? (b.LastName || '').toLowerCase()
      : (b.FirstName || '').toLowerCase();
    return nameA.localeCompare(nameB);
  };
};

const sortYears = (isReverse) => {
  if (isReverse) {
    return function (a, b) {
      return compareYears(b.Year, a.Year);
    };
  }
  return function (a, b) {
    return compareYears(a.Year, b.Year);
  };
};

const compareYears = (yearA, yearB) => {
  const yearIntA = parseInt(yearA);
  const yearIntB = parseInt(yearB);

  if (!isNaN(yearIntA) && !isNaN(yearIntB)) {
    return yearIntA - yearIntB;
  } else if (!isNaN(yearIntA)) {
    return -1;
  } else if (!isNaN(yearIntB)) {
    return 1;
  } else {
    return (yearA || '').localeCompare(yearB || '');
  }
};

const sortPositions = (isReverse) => {
  if (isReverse) {
    return function (a, b) {
      return (b.Position || '').localeCompare(a.Position || '');
    };
  }
  return function (a, b) {
    return (a.Position || '').localeCompare(b.Position || '');
  };
};

const sortAge = (isReverse) => {
  const ageOf = (item) => getProfileAgeNumber(item.DOB, item.Age) ?? 0;
  if (isReverse) {
    return function (a, b) {
      return ageOf(b) - ageOf(a);
    };
  }
  return function (a, b) {
    return ageOf(a) - ageOf(b);
  };
};

const sortState = (isReverse) => {
  if (isReverse) {
    return function (a, b) {
      return (b.hometown_State_Country || '').localeCompare(a.hometown_State_Country || '');
    };
  }
  return function (a, b) {
    return (a.hometown_State_Country || '').localeCompare(b.hometown_State_Country || '');
  };
};

const sortDateCreated = (isReverse) => {
  return function (a, b) {
    const dateA = a.dateCreated?.toDate ? a.dateCreated.toDate() : new Date(a.dateCreated);
    const dateB = b.dateCreated?.toDate ? b.dateCreated.toDate() : new Date(b.dateCreated);

    const isDateAValid = !isNaN(dateA);
    const isDateBValid = !isNaN(dateB);

    if (isDateAValid && isDateBValid) {
      return isReverse ? dateB - dateA : dateA - dateB;
    } else if (isDateAValid && !isDateBValid) {
      return -1;
    } else if (!isDateAValid && isDateBValid) {
      return 1;
    }
    return 0;
  };
};

const projectionRankAsc = {
  "HM+": 1, HM: 2, "HM-": 3,
  "MM+": 4, MM: 5, "MM-": 6,
  "LM+": 7, LM: 8, "LM-": 9
};

const projectionRankDesc = {
  "LM-": 1, LM: 2, "LM+": 3,
  "MM-": 4, MM: 5, "MM+": 6,
  "HM-": 7, HM: 8, "HM+": 9
};

const sortProjection = (isReverse) => {
  const rank = isReverse ? projectionRankDesc : projectionRankAsc;
  return (a, b) => {
    const getRank = proj => proj ? rank[proj] : Number.MAX_SAFE_INTEGER;
    
    if (!a.Prhigh && !b.Prhigh) return 0;
    if (!a.Prhigh) return 1;
    if (!b.Prhigh) return -1;

    const rankAHigh = getRank(a.Prhigh);
    const rankBHigh = getRank(b.Prhigh);
    if (rankAHigh !== rankBHigh) return rankAHigh - rankBHigh;

    const aIsEqual = a.Prlow === a.Prhigh;
    const bIsEqual = b.Prlow === b.Prhigh;
    if (aIsEqual && !bIsEqual) return -1;
    if (!aIsEqual && bIsEqual) return 1;

    const rankALow = getRank(a.Prlow);
    const rankBLow = getRank(b.Prlow);
    return rankALow - rankBLow;
  };
};

const sortPIScore = (isReverse) => {
  if (isReverse) {
    return function (a, b) {
      const scoreA = parseFloat(a.overallScore) || 0;
      const scoreB = parseFloat(b.overallScore) || 0;
      return scoreB - scoreA;
    };
  }
  return function (a, b) {
    const scoreA = parseFloat(a.overallScore) || 0;
    const scoreB = parseFloat(b.overallScore) || 0;
    return scoreA - scoreB;
  };
};

// Sort by numeric attribute; missing (0 or falsy) values sort last
const sortByNumeric = (getValue, isReverse) => {
  return (a, b) => {
    const valA = getValue(a);
    const valB = getValue(b);
    const hasA = valA != null && valA > 0;
    const hasB = valB != null && valB > 0;
    if (!hasA && !hasB) return 0;
    if (!hasA) return 1;
    if (!hasB) return -1;
    return isReverse ? valB - valA : valA - valB;
  };
};
const sortHeight = (isReverse) => sortByNumeric((p) => p.heightIn || 0, isReverse);
const sortWeight = (isReverse) => sortByNumeric((p) => p.weightNum || 0, isReverse);
const sortWingspan = (isReverse) => sortByNumeric((p) => p.wingspanIn || 0, isReverse);

// Optimized sorting function with memoization
const sortProfiles = (profiles, sortBy) => {
  if (!profiles || !profiles.length) return profiles;
  
  let sortFn;
  switch (sortBy) {
    case 'NDSC': sortFn = sortNames(true, false); break;
    case 'NASC': sortFn = sortNames(false, false); break;
    case 'LDSC': sortFn = sortNames(true, true); break;
    case 'LASC': sortFn = sortNames(false, true); break;
    case 'YASC': sortFn = sortYears(true); break;
    case 'YDSC': sortFn = sortYears(false); break;
    case 'PASC': sortFn = sortPositions(false); break;
    case 'PDSC': sortFn = sortPositions(true); break;
    case 'AASC': sortFn = sortAge(true); break;
    case 'ADSC': sortFn = sortAge(false); break;
    case 'SASC': sortFn = sortState(false); break;
    case 'SDSC': sortFn = sortState(true); break;
    case 'TASC': sortFn = sortTeams(false); break;
    case 'TDSC': sortFn = sortTeams(true); break;
    case 'PRASC': sortFn = sortProjection(false); break;
    case 'PRDSC': sortFn = sortProjection(true); break;
    case 'DCASC': sortFn = sortDateCreated(false); break;
    case 'DCDSC': sortFn = sortDateCreated(true); break;
    case 'PIASC': sortFn = sortPIScore(false); break;
    case 'PIDSC': sortFn = sortPIScore(true); break;
    case 'HASC': sortFn = sortHeight(false); break;
    case 'HDSC': sortFn = sortHeight(true); break;
    case 'WASC': sortFn = sortWeight(false); break;
    case 'WDSC': sortFn = sortWeight(true); break;
    case 'WSASC': sortFn = sortWingspan(false); break;
    case 'WSDSC': sortFn = sortWingspan(true); break;
    default: sortFn = sortNames(false, true); // Default: Last Name ASC
  }

  return [...profiles].sort(sortFn);
};

// Helper function to render custom SVG icons
// options.pill: compact size for Smart Search role pills; options.fillColor: icon color (default white)
const renderRoleIcon = (svgCode, roleName, isTableIcon = false, options = {}) => {
  const { pill = false, fillColor } = options;
  const resolvedFill = fillColor != null ? fillColor : "white";

  if (!svgCode || !svgCode.trim()) return null;

  // Basic validation to ensure it's SVG content
  if (!svgCode.includes("<svg")) return null;

  let iconSize;
  let viewBoxSize;
  if (pill) {
    iconSize = 14;
    viewBoxSize = 22;
  } else if (isTableIcon) {
    iconSize = 22;
    viewBoxSize = 22;
  } else {
    const isLargerIcon = roleName === "Facilitator" || roleName === "High Motor";
    iconSize = isLargerIcon ? 70 : 60;
    viewBoxSize = isLargerIcon ? 70 : 60;
  }

  const processedSvg = svgCode.replace(
    /<svg([^>]*)>/g,
    `<svg$1 width="${iconSize}" height="${iconSize}" viewBox="0 0 ${viewBoxSize} ${viewBoxSize}" style="display: block; width: 100%; height: 100%; margin: 0; padding: 0; fill: ${resolvedFill}; color: ${resolvedFill};">`
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: `${iconSize}px`,
        height: `${iconSize}px`,
        verticalAlign: "middle",
        overflow: "hidden",
        flexShrink: 0,
        margin: 0,
        padding: 0,
      }}
      dangerouslySetInnerHTML={{ __html: processedSvg }}
    />
  );
};


// Memoized extraction function - created once outside the filter function
const extractLocationCode = (teamName) => {
  if (!teamName || typeof teamName !== 'string') return null;
  
  // Try pattern: "Team Name (ESP)" or "Team Name (CA)" - case insensitive, at end of string
  const parenMatch = teamName.trim().match(/\(([A-Z]{2,3})\)\s*$/i);
  if (parenMatch) return parenMatch[1].toUpperCase();
  
  // Try pattern: "Team Name, ESP" or "Team Name, CA" - case insensitive, at end of string
  const commaMatch = teamName.trim().match(/,\s*([A-Z]{2,3})\s*$/i);
  if (commaMatch) return commaMatch[1].toUpperCase();
  
  return null;
};

// Optimized filtering function
const filterProfiles = (profiles, filterBy, heightRange, weightRange, wingspanRange, search, roles, teamsLookupMap) => {
  if (!profiles || !profiles.length) return profiles;
  
  // Early return if no filters applied
  const hasFilters = Object.entries(filterBy).some(([key, value]) => {
    if (key === 'intlPlayer') {
      return value === true; // Check boolean value
    }
    return Array.isArray(value) && value.length > 0;
  }) ||
                    search ||
                    heightRange[0] !== DEFAULT_HEIGHT[0] || heightRange[1] !== DEFAULT_HEIGHT[1] ||
                    weightRange[0] !== DEFAULT_WEIGHT[0] || weightRange[1] !== DEFAULT_WEIGHT[1] ||
                    wingspanRange[0] !== DEFAULT_WINGSPAN[0] || wingspanRange[1] !== DEFAULT_WINGSPAN[1];
  
  if (!hasFilters) return profiles;
  
  return profiles.filter((p) => {
    // Multi-select filters
    const countryMatch = 
      !filterBy.country || 
      filterBy.country.length === 0 || 
      (p.Country && filterBy.country.includes(p.Country));
      
    const stateandcountriesMatch =
      !filterBy.stateandcountry || 
      filterBy.stateandcountry.length === 0 || 
      (p.hometown_State_Country && filterBy.stateandcountry.includes(p.hometown_State_Country));
      
    const positionMatch =
      !filterBy.position || 
      filterBy.position.length === 0 || 
      (p.Position && filterBy.position.includes(p.Position));
      
    const yearMatch =
      !filterBy.year || 
      filterBy.year.length === 0 || 
      (p.Year && filterBy.year.includes(p.Year));
      
    const teamMatch =
      !filterBy.team || 
      filterBy.team.length === 0 || 
      (() => {
        if (!p.Team) return false;
        
        // Fast lookup using Map - O(1) instead of O(n)
        const getTeamIdentifier = (teamName) => {
          if (!teamName || !teamsLookupMap || teamsLookupMap.size === 0) return null;
          // Try exact match first
          let teamData = teamsLookupMap.get(teamName);
          if (teamData) return teamData.identifier;
          
          // Try without location code
          const teamNameWithoutLocation = teamName.replace(/\s*\([A-Z]{2,3}\)$/i, '').replace(/,\s*[A-Z]{2,3}$/i, '');
          teamData = teamsLookupMap.get(teamNameWithoutLocation);
          return teamData ? teamData.identifier : null;
        };
        
        // Check if any selected filter matches
        return filterBy.team.some(selectedValue => {
          if (!selectedValue) return false;
          
          // If it's a location filter (prefixed with "STATE:" or "COUNTRY:")
          if (selectedValue.startsWith('STATE:')) {
            const stateCode = selectedValue.replace('STATE:', '').toUpperCase();
            const locationCode = extractLocationCode(p.Team);
            
            // States are 2 letters - if location code is not 2 letters, it's not a state
            if (!locationCode || locationCode.length !== 2) return false;
            
            // Check if it's an INTL team using fast lookup
            const teamIdentifier = getTeamIdentifier(p.Team);
            if (teamIdentifier === "INTL" || teamIdentifier === "INT'L") {
              // It's an INTL team, so don't match states
              return false;
            }
            
            // Match if location code matches (and it's not an INTL team)
            return locationCode === stateCode;
          } else if (selectedValue.startsWith('COUNTRY:')) {
            const countryCode = selectedValue.replace('COUNTRY:', '').toUpperCase();
            const locationCode = extractLocationCode(p.Team);
            
            // For country codes, match if the location code matches
            // This will match any team ending with (ESP), (ITA), etc.
            if (!locationCode) return false;
            
            // Country codes can be 2 or 3 letters, so just check if they match
            return locationCode === countryCode;
          } else {
            // Team filter value is the full unique team name (includes location suffix).
            return (p.Team || "").trim() === String(selectedValue).trim();
          }
        });
      })();
      
    const aauTeamMatch =
      !filterBy.aauTeam || 
      filterBy.aauTeam.length === 0 || 
      (() => {
        if (!p.aauTeam) return false;
        
        // Check if any selected filter matches
        return filterBy.aauTeam.some(selectedValue => {
          // If it's a state filter (prefixed with "STATE:")
          if (selectedValue.startsWith('STATE:')) {
            const stateCode = selectedValue.replace('STATE:', '').toUpperCase();
            // Extract state from player's AAU team name (2 letters only for states)
            const locationCode = extractLocationCode(p.aauTeam);
            if (!locationCode || locationCode.length !== 2) return false;
            return locationCode === stateCode;
          } else {
            // It's a team name filter - exact match
            return (p.aauTeam || "").trim() === String(selectedValue).trim();
          }
        });
      })();
      
    const statusMatch =
      !filterBy.status || 
      filterBy.status.length === 0 || 
      (p.Status && filterBy.status.includes(p.Status));
      
    const affiliationMatch =
      !filterBy.affliation || 
      filterBy.affliation.length === 0 || 
      (p.aauAffil && filterBy.affliation.includes(p.aauAffil));
      
    const rolesMatch =
      !filterBy.roles || 
      filterBy.roles.length === 0 || 
      (() => {
        // Check if player has any of the selected roles using role summaries
        if (!p.roleSummaries || !p.roleSummaries.roleCounts || Object.keys(p.roleSummaries.roleCounts).length === 0) {
          return false; // No roles data available
        }
        
        // Check if player has ALL of the selected roles with final count > 0
        return filterBy.roles.every(roleName => {
          // roleName is now the actual role name (e.g., "Creator", "Shooter")
          if (!roleName) return false;
          
          // Access the nested structure: roleSummaries.roleCounts[roleName].finalCount
          const roleData = p.roleSummaries.roleCounts[roleName];
          const finalCount = roleData ? roleData.finalCount : 0;
          
          console.log(`Checking role ${roleName} for player ${p.FirstName} ${p.LastName}:`, roleData, `finalCount:`, finalCount);
          
          return finalCount > 0;
        });
      })();

    const intlPlayerMatch =
      !filterBy.intlPlayer || 
      (p.intlPlayer === true);

    // Range filters: when range is non-default, exclude players with missing values (AND logic)
    const heightIn = p.heightIn || 0;
    const heightRangeActive = heightRange[0] !== DEFAULT_HEIGHT[0] || heightRange[1] !== DEFAULT_HEIGHT[1];
    const heightOk = !heightRangeActive || (heightIn > 0 && heightIn >= heightRange[0] && heightIn <= heightRange[1]);

    const weight = p.weightNum || 0;
    const weightRangeActive = weightRange[0] !== DEFAULT_WEIGHT[0] || weightRange[1] !== DEFAULT_WEIGHT[1];
    const weightOk = !weightRangeActive || (weight > 0 && weight >= weightRange[0] && weight <= weightRange[1]);

    const wingspanIn = p.wingspanIn || 0;
    const wingspanRangeActive = wingspanRange[0] !== DEFAULT_WINGSPAN[0] || wingspanRange[1] !== DEFAULT_WINGSPAN[1];
    const wingspanOk = !wingspanRangeActive || (wingspanIn > 0 && wingspanIn >= wingspanRange[0] && wingspanIn <= wingspanRange[1]);

    // Search filter
    const searchMatch = !search || (() => {
      const fullName = `${p.FirstName || ''} ${p.LastName || ''}`.toLowerCase();
      return fullName.includes(search.toLowerCase());
    })();

    return (
      countryMatch &&
      stateandcountriesMatch &&
      positionMatch &&
      yearMatch &&
      teamMatch &&
      aauTeamMatch &&
      statusMatch &&
      affiliationMatch &&
      rolesMatch &&
      intlPlayerMatch &&
      heightOk &&
      weightOk &&
      wingspanOk &&
      searchMatch
    );
  });
};

// Function to fetch role icons and explanations
const fetchRoleData = async (roleNames) => {
  try {
    const explanations = {};
    const icons = {};
    
    for (const roleName of roleNames) {
      try {
        const response = await db
          .collection("roles-dropdown")
          .where("name", "==", roleName)
          .get();
        
        if (!response.empty) {
          const roleDoc = response.docs[0];
          const roleData = roleDoc.data();
          explanations[roleName] = roleData.explanation || "";
          icons[roleName] = roleData.icon || "";
        } else {
          explanations[roleName] = "";
          icons[roleName] = "";
        }
      } catch (error) {
        console.log(`Error fetching data for ${roleName}:`, error);
        explanations[roleName] = "";
        icons[roleName] = "";
      }
    }
    
    return { explanations, icons };
  } catch (error) {
    console.log("Error fetching role data:", error);
    return { explanations: {}, icons: {} };
  }
};

export const PlayerProfile = () => {
  const { isAdmin, isSuperAdmin, getUserInfoData } = useAuth();
  const location = useLocation();
  const [countries, setCountries] = useState([]);
  const [stateandcountries, setStateCountries] = useState([]);
  const [positions, setPositions] = useState([]);
  const [years, setYears] = useState([]);
  const [aauTeams, setAAUTeams] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rawTeamsData, setRawTeamsData] = useState([]); // Store raw team data for lookup
  const [teamsLookupMap, setTeamsLookupMap] = useState(new Map()); // Fast lookup map for teams
  const [affliations, setAffliations] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleIcons, setRoleIcons] = useState({});
  const [roleExplanations, setRoleExplanations] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [allPlayerProfiles, setAllPlayerProfiles] = useState([]); // All profiles from Firebase
  const [sortBy, setSortBy] = useState("LASC");
  const [localSearch, setLocalSearch] = useState(""); // Local search state
  const [search, setSearch] = useState(""); // Actual search filter

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalResults, setTotalResults] = useState(0);

  // Height/weight sliders
  const [heightRange, setHeightRange] = useState(DEFAULT_HEIGHT);
  const [weightRange, setWeightRange] = useState(DEFAULT_WEIGHT);
  const [wingspanRange, setWingspanRange] = useState(DEFAULT_WINGSPAN);

  // Filter state
  const [filterBy, setFilterBy] = useState({
    country: [],
    position: [],
    year: [],
    team: [],
    aauTeam: [],
    affliation: [],
    stateandcountry: [],
    status: [],
    roles: [],
    intlPlayer: false,
  });

  // "Request a Player" modal state
  const initialRequestPlayerForm = {
    requesterFirstName: "",
    requesterLastName: "",
    requesterEmail: "",
    playerFirstName: "",
    playerLastName: "",
    playerTeam: "",
    notes: "",
  };
  const [showRequestPlayerModal, setShowRequestPlayerModal] = useState(false);
  const [requestPlayerForm, setRequestPlayerForm] = useState(initialRequestPlayerForm);
  const [requestPlayerErrors, setRequestPlayerErrors] = useState({});
  const [isSubmittingRequestPlayer, setIsSubmittingRequestPlayer] = useState(false);

  const toast = useRef(null);
  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= 768;

  // Create debounced search function
  const debouncedSearch = useCallback(
    debounce(value => {
      setSearch(value);
      setCurrentPage(1);
    }, 300),
    []
  );

  // Open "Request a Player" modal when coming from header link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("requestPlayer") === "true") {
      setShowRequestPlayerModal(true);
    }
  }, [location.search]);

  // Fetch all player profiles once - this is the key optimization
  useEffect(() => {
    const fetchAllProfiles = async () => {
      try {
        setIsLoading(true);
        console.log("Fetching all player profiles from Firebase...");
        
        // Single Firebase read to get all profiles
        const snapshot = await db.collection("player-profile").get();
        
        let processedData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: data.ID || doc.id, // Ensure ID is preserved
            heightIn: feetInchesToInches(data.Height),
            weightNum: parseFloat(data.Weight) || 0,
            wingspanIn: feetInchesToInches(data.Wingspan),
          };
        });
        
        console.log("Processing player profiles:", processedData.length, "items");
        
        // Apply viewing privileges filter
        if (isAdmin() || isSuperAdmin()) {
          console.log("Admin or Super Admin detected, showing all profiles");
        } else {
          console.log("Non-admin user detected, filtering profiles by viewing privileges");
          const userMemberType = getUserInfoData()?.memberType;
          processedData = processedData.filter(item => 
            item.viewing_privileges?.includes(userMemberType)
          );
        }
        
        // Fetch role summaries for efficient filtering
        console.log("Fetching role summaries for efficient filtering...");
        try {
          const roleSummariesSnapshot = await db.collection("player-role-summaries").get();
          const roleSummaries = {};
          
          roleSummariesSnapshot.docs.forEach(doc => {
            roleSummaries[doc.id] = doc.data();
          });
          
                  // Attach role summaries to player profiles
        processedData = processedData.map(profile => ({
          ...profile,
          roleSummaries: roleSummaries[profile.id] || {}
        }));
        
        console.log("Role summaries attached to profiles");
        
        // Debug: Show role summaries for the specific player
        const debugPlayer = processedData.find(p => p.id === "3mDGz01AK4oWzNsAsy4k");
        if (debugPlayer) {
          console.log("Debug player role summaries:", debugPlayer.roleSummaries);
        }
        } catch (roleError) {
          console.warn("Could not fetch role summaries, roles filter will be disabled:", roleError);
          // Continue without role summaries
        }
        
        console.log("Final processed data length:", processedData.length);
        setAllPlayerProfiles(processedData);
        setIsLoading(false);
        
      } catch (error) {
        console.error("Error fetching player profiles:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load player profiles",
          life: 3000,
        });
        setIsLoading(false);
      }
    };

    fetchAllProfiles();
  }, [isAdmin, isSuperAdmin, getUserInfoData]);

  // Real-time listener for player-role-summaries to update player roles when reports change
  useEffect(() => {
    if (!allPlayerProfiles || allPlayerProfiles.length === 0) return;

    console.log("Setting up real-time listener for player-role-summaries");
    
    const unsubscribe = db.collection("player-role-summaries")
      .onSnapshot(
        (snapshot) => {
          try {
            console.log("player-role-summaries collection updated, refreshing role data");
            
            // Build a map of updated role summaries
            const updatedRoleSummaries = {};
            snapshot.docs.forEach(doc => {
              updatedRoleSummaries[doc.id] = doc.data();
            });

            // Update allPlayerProfiles with new role summaries
            setAllPlayerProfiles(prevProfiles => {
              return prevProfiles.map(profile => ({
                ...profile,
                roleSummaries: updatedRoleSummaries[profile.id] || {}
              }));
            });

            console.log("Player role summaries updated in real-time");
          } catch (error) {
            console.error("Error updating role summaries from real-time listener:", error);
          }
        },
        (error) => {
          console.error("Error listening to player-role-summaries:", error);
        }
      );

    // Cleanup listener on unmount
    return () => {
      console.log("Cleaning up player-role-summaries listener");
      unsubscribe();
    };
  }, [allPlayerProfiles.length]); // Only re-setup if the number of profiles changes

  // Memoized filtered and sorted results - this is the key performance optimization
  const filteredAndSortedProfiles = useMemo(() => {
    if (!allPlayerProfiles || allPlayerProfiles.length === 0) return [];
    
    // Apply all filters
    let result = filterProfiles(allPlayerProfiles, filterBy, heightRange, weightRange, wingspanRange, search, roles, teamsLookupMap);
    
    // Apply sorting
    result = sortProfiles(result, sortBy);
    
    return result;
  }, [allPlayerProfiles, filterBy, heightRange, weightRange, wingspanRange, search, sortBy, roles, teamsLookupMap]);

  // Update total results when filtered results change
  useEffect(() => {
    setTotalResults(filteredAndSortedProfiles.length);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filteredAndSortedProfiles]);

  // Get paginated results
  const getPaginatedResults = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedProfiles.slice(startIndex, endIndex);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilterBy({
      country: [],
      position: [],
      year: [],
      team: [],
      aauTeam: [],
      affliation: [],
      stateandcountry: [],
      status: [],
      roles: [],
      intlPlayer: false,
    });
    setHeightRange(DEFAULT_HEIGHT);
    setWeightRange(DEFAULT_WEIGHT);
    setWingspanRange(DEFAULT_WINGSPAN);
    setLocalSearch("");
    setSearch("");
    setCurrentPage(1);
  };

  // "Request a Player" helpers
  const openRequestPlayerModal = () => {
    setShowRequestPlayerModal(true);
  };

  const closeRequestPlayerModal = () => {
    setShowRequestPlayerModal(false);
  };

  const handleRequestPlayerChange = (e) => {
    const { name, value } = e.target;
    setRequestPlayerForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateRequestPlayerForm = () => {
    const errors = {};
    if (!requestPlayerForm.requesterFirstName.trim()) {
      errors.requesterFirstName = "First name is required";
    }
    if (!requestPlayerForm.requesterLastName.trim()) {
      errors.requesterLastName = "Last name is required";
    }
    if (!requestPlayerForm.requesterEmail.trim()) {
      errors.requesterEmail = "Email is required";
    } else if (!isValidEmail(requestPlayerForm.requesterEmail.trim())) {
      errors.requesterEmail = "Invalid email address";
    }
    if (!requestPlayerForm.playerFirstName.trim()) {
      errors.playerFirstName = "Player first name is required";
    }
    if (!requestPlayerForm.playerLastName.trim()) {
      errors.playerLastName = "Player last name is required";
    }
    if (!requestPlayerForm.playerTeam.trim()) {
      errors.playerTeam = "Team is required";
    }
    return errors;
  };

  const handleRequestPlayerSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingRequestPlayer) return;

    const errors = validateRequestPlayerForm();
    setRequestPlayerErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmittingRequestPlayer(true);
    try {
      const requestedAt = new Date();
      const subject = "New Player Request";
      const bodyLines = [
        "A new player has been requested.",
        "",
        "Requested By:",
        `- First Name: ${requestPlayerForm.requesterFirstName.trim()}`,
        `- Last Name: ${requestPlayerForm.requesterLastName.trim()}`,
        `- Email: ${requestPlayerForm.requesterEmail.trim()}`,
        "",
        "Player Details:",
        `- First Name: ${requestPlayerForm.playerFirstName.trim()}`,
        `- Last Name: ${requestPlayerForm.playerLastName.trim()}`,
        `- Team: ${requestPlayerForm.playerTeam.trim()}`,
        "",
        "Notes:",
        requestPlayerForm.notes.trim() || "N/A",
        "",
        `Requested At: ${requestedAt.toLocaleString()}`,
      ];

      await db.collection("mail").add({
        to: ["proinsightnotifications@gmail.com"],
        message: {
          subject,
          text: bodyLines.join("\n"),
          html: bodyLines.map((line) => `<div>${line}</div>`).join(""),
        },
      });

      toast.current?.show({
        severity: "success",
        summary: "Request Sent",
        detail: "Thank you! Our team will review your request.",
        life: 4000,
      });

      setRequestPlayerForm(initialRequestPlayerForm);
      setRequestPlayerErrors({});
      setShowRequestPlayerModal(false);
    } catch (error) {
      console.error("Error sending player request email:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to send player request. Please try again.",
        life: 4000,
      });
    } finally {
      setIsSubmittingRequestPlayer(false);
    }
  };

  // Function to rebuild role summaries for all players (admin only)
  const rebuildAllRoleSummaries = async () => {
    if (!isAdmin() && !isSuperAdmin()) {
      toast.current?.show({
        severity: "warn",
        summary: "Access Denied",
        detail: "Only administrators can rebuild role summaries",
        life: 3000,
      });
      return;
    }

    try {
      // Confirmation dialog
      const confirmed = window.confirm(
        `This will rebuild role summaries for ${allPlayerProfiles.length} players.\n\n` +
        `This process may take several minutes and will update the database.\n\n` +
        `Are you sure you want to continue?`
      );
      
      if (!confirmed) return;

      toast.current?.show({
        severity: "info",
        summary: "Starting Rebuild",
        detail: `Rebuilding role summaries for ${allPlayerProfiles.length} players...`,
        life: 3000,
      });

      const totalPlayers = allPlayerProfiles.length;
      let processed = 0;
      let successful = 0;
      let failed = 0;
      const startTime = Date.now();

      // Process in smaller batches to avoid overwhelming Firebase
      const batchSize = 5;
      const batches = [];
      
      for (let i = 0; i < totalPlayers; i += batchSize) {
        batches.push(allPlayerProfiles.slice(i, i + batchSize));
      }

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Process batch concurrently
        const batchPromises = batch.map(async (player) => {
          try {
            await buildPlayerRoleSummary(player.id);
            successful++;
            return { success: true, player: player.FirstName + ' ' + player.LastName };
          } catch (error) {
            failed++;
            console.error(`Failed to update ${player.FirstName} ${player.LastName}:`, error);
            return { success: false, player: player.FirstName + ' ' + player.LastName, error };
          }
        });

        // Wait for batch to complete
        await Promise.allSettled(batchPromises);
        processed += batch.length;

        // Progress update every batch
        const elapsed = Math.round((Date.now() - startTime) / 1000);
        const estimatedTotal = Math.round((elapsed / processed) * totalPlayers);
        const remaining = Math.max(0, estimatedTotal - elapsed);
        
        toast.current?.show({
          severity: "info",
          summary: "Progress Update",
          detail: `Processed ${processed}/${totalPlayers} players (${Math.round(processed/totalPlayers*100)}%)\n` +
                   `Successful: ${successful}, Failed: ${failed}\n` +
                   `Elapsed: ${elapsed}s, Estimated remaining: ${remaining}s`,
          life: 3000,
        });

        // Small delay between batches to avoid rate limiting
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      const totalTime = Math.round((Date.now() - startTime) / 1000);
      
      toast.current?.show({
        severity: "success",
        summary: "Rebuild Complete",
        detail: `Role summaries rebuilt for ${totalPlayers} players\n` +
                 `Successful: ${successful}, Failed: ${failed}\n` +
                 `Total time: ${totalTime} seconds`,
        life: 5000,
      });

      // Show detailed results in console
      console.log(`Role Summary Rebuild Complete:`);
      console.log(`Total players: ${totalPlayers}`);
      console.log(`Successful: ${successful}`);
      console.log(`Failed: ${failed}`);
      console.log(`Total time: ${totalTime} seconds`);
      console.log(`Average time per player: ${(totalTime / totalPlayers).toFixed(2)} seconds`);

      // Refresh the data after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error("Error rebuilding role summaries:", error);
      toast.current?.show({
        severity: "error",
        summary: "Rebuild Failed",
        detail: `Error: ${error.message}\nCheck console for details.`,
        life: 5000,
      });
    }
  };

  // Function to test role summary for a specific player (admin only)
  const testPlayerRoleSummary = async () => {
    if (!isAdmin() && !isSuperAdmin()) {
      toast.current?.show({
        severity: "warn",
        summary: "Access Denied",
        detail: "Only administrators can test role summaries",
        life: 3000,
      });
      return;
    }

    try {
      const playerId = "3mDGz01AK4oWzNsAsy4k"; // The specific player you mentioned
      console.log(`Testing role summary for player: ${playerId}`);
      
      const result = await buildPlayerRoleSummary(playerId);
      console.log(`Test result for player ${playerId}:`, result);
      
      toast.current?.show({
        severity: "success",
        summary: "Test Complete",
        detail: `Check console for role summary details`,
        life: 3000,
      });
    } catch (error) {
      console.error("Error testing role summary:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to test role summary",
        life: 3000,
      });
    }
  };

  // Function to rebuild role summary for a specific player (admin only)
  const rebuildSinglePlayerSummary = async () => {
    if (!isAdmin() && !isSuperAdmin()) {
      toast.current?.show({
        severity: "warn",
        summary: "Access Denied",
        detail: "Only administrators can rebuild role summaries",
        life: 3000,
      });
      return;
    }

    try {
      const playerId = "3mDGz01AK4oWzNsAsy4k"; // The specific player you mentioned
      const player = allPlayerProfiles.find(p => p.id === playerId);
      
      if (!player) {
        toast.current?.show({
          severity: "warn",
          summary: "Player Not Found",
          detail: `Player with ID ${playerId} not found in current data`,
          life: 3000,
        });
        return;
      }

      toast.current?.show({
        severity: "info",
        summary: "Rebuilding Summary",
        detail: `Rebuilding role summary for ${player.FirstName} ${player.LastName}...`,
        life: 3000,
      });

      const startTime = Date.now();
      const result = await buildPlayerRoleSummary(playerId);
      const totalTime = Math.round((Date.now() - startTime) / 1000);

      console.log(`Single player rebuild complete for ${player.FirstName} ${player.LastName}:`, result);
      
      toast.current?.show({
        severity: "success",
        summary: "Rebuild Complete",
        detail: `Role summary rebuilt for ${player.FirstName} ${player.LastName}\n` +
                 `Time: ${totalTime} seconds\n` +
                 `Check console for details`,
        life: 4000,
      });

      // Refresh the data after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error("Error rebuilding single player summary:", error);
      toast.current?.show({
        severity: "error",
        summary: "Rebuild Failed",
        detail: `Error: ${error.message}\nCheck console for details.`,
        life: 5000,
      });
    }
  };

  // Function to debug role name casing issues
  const debugRoleNames = async () => {
    if (!isAdmin() && !isSuperAdmin()) {
      toast.current?.show({
        severity: "warn",
        summary: "Access Denied",
        detail: "Only administrators can debug role names",
        life: 3000,
      });
      return;
    }

    try {
      console.log("=== ROLE NAME DEBUGGING ===");
      
      // Check what's in the roles state
      console.log("Roles from state:", roles);
      
      // Check what's in the roles-dropdown collection
      const rolesSnapshot = await db.collection("roles-dropdown").get();
      const rolesFromDB = rolesSnapshot.docs.map(doc => doc.data());
      
      console.log("Roles from roles-dropdown collection:", rolesFromDB);
      
      // Look specifically for 3-and-D related roles
      const threeAndDRoles = rolesFromDB.filter(role => 
        role.name && role.name.toLowerCase().includes('3') && role.name.toLowerCase().includes('and') && role.name.toLowerCase().includes('d')
      );
      
      console.log("3-and-D related roles found:", threeAndDRoles);
      
      // Check the current player's role summary
      const playerId = "3mDGz01AK4oWzNsAsy4k";
      const summarySnapshot = await db.collection("player-role-summaries").doc(playerId).get();
      
      if (summarySnapshot.exists) {
        const summary = summarySnapshot.data();
        console.log("Current role summary from database:", summary);
        console.log("Role keys in summary:", Object.keys(summary.roleCounts || {}));
      }
      
      toast.current?.show({
        severity: "success",
        summary: "Debug Complete",
        detail: "Check console for role name analysis",
        life: 3000,
      });
      
    } catch (error) {
      console.error("Error debugging role names:", error);
      toast.current?.show({
        severity: "error",
        summary: "Debug Failed",
        detail: `Error: ${error.message}`,
        life: 3000,
      });
    }
  };

  // Initialize dropdowns
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const dropdownCollections = [
          { name: "nationality-dropdown", setter: setCountries, processor: data => {
            data.sort((a, b) => 
              a.name === "United States of America" ? -1 : 
              b.name === "United States of America" ? 1 : 
              a.name.localeCompare(b.name)
            );
            return data;
          }},
          { name: "state-country-dropdown", setter: setStateCountries, processor: data => {
            data.sort((a, b) => {
              const isAState = a.name.length === 2 || a.name === "D.C.";
              const isBState = b.name.length === 2 || b.name === "D.C.";
              if (isAState && isBState) return a.name.localeCompare(b.name);
              if (isAState) return -1;
              if (isBState) return 1;
              return a.name.localeCompare(b.name);
            });
            return data;
          }},
          { name: "position-dropdown", setter: setPositions, processor: data => {
            const positionOrder = ["Guard", "Wing", "Forward", "Big"];
            data.sort((a, b) => 
              positionOrder.indexOf(a.name) - positionOrder.indexOf(b.name)
            );
            return data;
          }},
          { name: "year-dropdown", setter: setYears, processor: data => {
            const intlYear = data.find(year => year.name === "INT'L");
            const otherYears = data.filter(year => year.name !== "INT'L");
            otherYears.sort((a, b) => parseInt(a.name) - parseInt(b.name));
            const intlForUi = intlYear
              ? {
                  ...intlYear,
                  name: "INTL",
                  value:
                    intlYear.value != null && intlYear.value !== ""
                      ? intlYear.value
                      : intlYear.name,
                }
              : null;
            return intlForUi ? [intlForUi, ...otherYears] : otherYears;
          }},
          { name: "school-dropdown", setter: setTeams, processor: data => {
            // Store raw team data for lookup in filter
            setRawTeamsData(data);
            
            // Create fast lookup Map for O(1) team identifier lookups
            const lookupMap = new Map();
            data.forEach(team => {
              // Store by team name
              if (team.name) lookupMap.set(team.name, team);
              // Store by team value
              if (team.value) lookupMap.set(team.value, team);
              // Also store without location code for flexible matching
              const nameWithoutLocation = team.name?.replace(/\s*\([A-Z]{2,3}\)$/i, '').replace(/,\s*[A-Z]{2,3}$/i, '');
              if (nameWithoutLocation && nameWithoutLocation !== team.name) {
                lookupMap.set(nameWithoutLocation, team);
              }
            });
            setTeamsLookupMap(lookupMap);
            
            // Extract US states from team names (format: "Team Name (CA)" or "Team Name, CA")
            const extractState = (teamName) => {
              // Try pattern: "Team Name (CA)"
              const parenMatch = teamName.match(/\(([A-Z]{2})\)$/);
              if (parenMatch) return parenMatch[1];
              
              // Try pattern: "Team Name, CA"
              const commaMatch = teamName.match(/,\s*([A-Z]{2})$/);
              if (commaMatch) return commaMatch[1];
              
              return null;
            };

            // Extract international country codes from INTL teams (format: "Team Name (ESP)" or "Team Name, ESP")
            const extractCountryCode = (teamName) => {
              // Try pattern: "Team Name (ESP)"
              const parenMatch = teamName.match(/\(([A-Z]{2,3})\)$/);
              if (parenMatch) return parenMatch[1];
              
              // Try pattern: "Team Name, ESP"
              const commaMatch = teamName.match(/,\s*([A-Z]{2,3})$/);
              if (commaMatch) return commaMatch[1];
              
              return null;
            };

            // Get all unique US states
            const states = new Set();
            // Get all unique international country codes from INTL teams
            const countryCodes = new Set();
            
            data.forEach(team => {
              if (team.identifier === "INTL" || team.identifier === "INT'L") {
                // Extract country code from international teams
                const countryCode = extractCountryCode(team.name);
                if (countryCode) {
                  countryCodes.add(countryCode);
                }
              } else {
                // Extract US state from other teams
                const state = extractState(team.name);
                if (state) {
                  states.add(state);
                }
              }
            });
            
            // Sort teams alphabetically
            const sortedTeams = [...data].sort((a, b) => a.name.localeCompare(b.name));
            
            // Add "Transfer Portal" for admin/super-admin at the beginning
            if (isAdmin() || isSuperAdmin()) {
              sortedTeams.unshift({ name: "Transfer Portal", value: "Transfer Portal" });
            }
            
            // Create grouped structure for PrimeReact MultiSelect
            const groupedOptions = [];
            
            // Add "Team" group with all teams
            groupedOptions.push({
              label: "Team",
              items: sortedTeams.map(team => ({
                name: (team.name || "").trim(),
                // Use full name as value so teams with the same base name but different
                // locations (e.g. "Central Catholic HS (OH)" vs "(OR)") stay distinct.
                value: (team.name || "").trim(),
                type: 'team'
              }))
            });
            
            // Add "Location" group with US states and international country codes
            const sortedStates = Array.from(states).sort();
            const sortedCountryCodes = Array.from(countryCodes).sort();
            
            const locationItems = [
              // US states first
              ...sortedStates.map(state => ({
                name: state,
                value: `STATE:${state}`,
                type: 'location'
              })),
              // International country codes after states
              ...sortedCountryCodes.map(countryCode => ({
                name: countryCode,
                value: `COUNTRY:${countryCode}`,
                type: 'location'
              }))
            ];
            
            groupedOptions.push({
              label: "Location",
              items: locationItems
            });
            
            return groupedOptions;
          }},
          { name: "AAUTeam-dropdown", setter: setAAUTeams, processor: data => {
            // Extract states from team names (format: "Team Name (CA)" or "Team Name, CA")
            const extractState = (teamName) => {
              // Try pattern: "Team Name (CA)"
              const parenMatch = teamName.match(/\(([A-Z]{2})\)$/);
              if (parenMatch) return parenMatch[1];
              
              // Try pattern: "Team Name, CA"
              const commaMatch = teamName.match(/,\s*([A-Z]{2})$/);
              if (commaMatch) return commaMatch[1];
              
              return null;
            };

            // Get all unique states
            const states = new Set();
            data.forEach(team => {
              const state = extractState(team.name);
              if (state) states.add(state);
            });
            
            // Sort teams alphabetically
            const sortedTeams = [...data].sort((a, b) => a.name.localeCompare(b.name));
            
            // Create grouped structure for PrimeReact MultiSelect
            const groupedOptions = [];
            
            // Add "Team" group with all teams
            groupedOptions.push({
              label: "Team",
              items: sortedTeams.map(team => ({
                name: (team.name || "").trim(),
                value: (team.name || "").trim(),
                type: 'team'
              }))
            });
            
            // Add "Location" group with states sorted alphabetically
            const sortedStates = Array.from(states).sort();
            groupedOptions.push({
              label: "Location",
              items: sortedStates.map(state => ({
                name: state,
                value: `STATE:${state}`, // Prefix to distinguish from team names
                type: 'location'
              }))
            });
            
            return groupedOptions;
          }},
          { name: "affiliation-dropdown", setter: setAffliations, processor: data => {
            const affiliationOrder = [
              "Nike", "adidas", "Under Armour", 
              "Puma/Pro16", "New Balance/P32", "Independent"
            ];
            data.sort((a, b) => 
              affiliationOrder.indexOf(a.name) - affiliationOrder.indexOf(b.name)
            );
            return data;
          }},
          { name: "status-dropdown", setter: setStatuses, processor: data => data },
          { name: "roles-dropdown", setter: setRoles, processor: data => {
            data.sort((a, b) => a.name.localeCompare(b.name));
            return data;
          }}
        ];

        for (const collection of dropdownCollections) {
          const response = await db.collection(collection.name).get();
          const data = response.docs.map(doc => doc.data());
          collection.setter(collection.processor(data));
        }

      } catch (error) {
        console.error("Error loading dropdowns:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load filter options",
          life: 3000,
        });
      }
    };

    fetchDropdowns();
  }, [isAdmin, isSuperAdmin]);

  // Fetch role icons and explanations when roles are loaded
  useEffect(() => {
    const loadRoleData = async () => {
      if (roles.length > 0) {
        try {
          const roleNames = roles.map(role => role.name);
          const { explanations, icons } = await fetchRoleData(roleNames);
          setRoleExplanations(explanations);
          setRoleIcons(icons);
        } catch (error) {
          console.error("Error loading role data:", error);
        }
      }
    };

    loadRoleData();
  }, [roles]);

  // Mobile Player Card Component
  const MobilePlayerCard = ({ item }) => {
    const windowSize = useWindowSize();
    const handleClick = () => {
      if (!item.id) {
        console.error("Player profile missing ID", item);
        return;
      }
      
      if (isAdmin() || isSuperAdmin()) {
        window.open(`/player-profile/detail/${item.id}`, "_blank");
      } else {
        window.open(`/player-profile/preview/${item.id}`, "_blank");
      }
    };

    // Determine badge color for mobile
    let badgeBg = '#000';
    if (windowSize.width <= 600) {
      if (item.Status && item.Status.toLowerCase() === 'undecided') {
        badgeBg = 'green';
      } else if (item.Status && ["committed", "signed"].includes(item.Status.toLowerCase())) {
        badgeBg = 'red';
      }
    }

    return (
      <div 
        className={styles.mobileCard}
        onClick={handleClick}
      >
        <div className={styles.cardHeader}>
          <div className={styles.playerImage}>
            <Image
              width="80px"
              height="80px"
              roundedCircle
              src={item?.Headshot ? item.Headshot : previewImage}
              onError={(e) => {
                e.target.src = previewImage;
              }}
            />
          </div>
          <div className={styles.playerInfo}>
            <div className={styles.nameContainer}>
              <h4 className={styles.playerName}>{item.FirstName} {item.LastName}</h4>
              <div className={styles.statusBadge} style={windowSize.width <= 600 ? { background: badgeBg } : {}}>
                {item.Status === "WTS" ? "WTS" : item.Status || '--'}
              </div>
            </div>
            
            <p className={styles.location}>
              {item.hometown_City && item.hometown_State_Country
                ? `${item.hometown_City}, ${item.hometown_State_Country}`
                : item.hometown_City || item.hometown_State_Country || '--'}
            </p>
            
            <div className={styles.teamContainer}>
              <h5 className={styles.teamName}>{item.Team || '--'}</h5>
              {item.aauTeam && (
                <div className={styles.aauTeam}>
                  {item.aauTeam}
                </div>
              )}
            </div>
          </div>
        </div>
  
        <div className={styles.projectionRange}>
          <p>Projection Range</p>
          <h4>
            {item.pRange && item.Prlow !== "" && item.Prhigh !== ""
              ? item.Prlow === item.Prhigh
                ? item.Prhigh
                : `${item.Prlow} to ${item.Prhigh}`
              : "-"}
          </h4>
        </div>
  
        <div className={styles.cardBody}>
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <p>HT</p>
              <h4>{formatFeetInches(item.Height) || '--'}</h4>
            </div>
            <div className={styles.statItem}>
              <p>WT</p>
              <h4>{formatWeightDisplay(item.Weight)}</h4>
            </div>
            <div className={styles.statItem}>
              <p>WS</p>
              <h4>{formatFeetInches(item.Wingspan) || '--'}</h4>
            </div>
          </div>
  
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <p>CLASS</p>
              <h4>{item.Year || '--'}</h4>
            </div>
            <div className={styles.statItem}>
              <p>HAND</p>
              <h4>{item.Hand || '--'}</h4>
            </div>
            <div className={styles.statItem}>
              <p>POS</p>
              <h4>{item.Position || '--'}</h4>
            </div>
          </div>
  
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <p>DOB</p>
              <h4>{item.DOB ? moment(item.DOB).format("MM/DD/YY") : '--'}</h4>
            </div>
            <div className={styles.statItem}>
              <p>AGE</p>
              <h4>
                {formatProfileAge(item.DOB, item.Age)}
              </h4>
            </div>
            <div className={styles.statItem}>
              <p>PI SCORE</p>
              <h4>
                {(isAdmin() || isSuperAdmin()) && item.overallScore
                  ? item.overallScore
                  : "Coming Soon"}
              </h4>
            </div>
          </div>
        </div>
        </div>
    );
  };

  // Sort options
  const sortByElements = [
    ...(isAdmin() || isSuperAdmin()
      ? [
          { name: "Date Created (Newest)", value: "DCDSC" },
          { name: "Date Created (Oldest)", value: "DCASC" },
          { name: "P.I. Score (low -> high)", value: "PIASC" },
          { name: "P.I. Score (high -> low)", value: "PIDSC" },
          
        ]
      : []),
    { name: "Projection Range (high -> low)", value: "PRASC" },
    { name: "Projection Range (low -> high)", value: "PRDSC" },
    { name: "Last Name (ASC)", value: "LASC" },
    { name: "Last Name (DSC)", value: "LDSC" },
    { name: "First Name (ASC)", value: "NASC" },
    { name: "First Name (DSC)", value: "NDSC" },
    { name: "Team (ASC)", value: "TASC" },
    { name: "Team (DSC)", value: "TDSC" },
    { name: "Year (ASC)", value: "YASC" },
    { name: "Year (DSC)", value: "YDSC" },
    { name: "Position (ASC)", value: "PASC" },
    { name: "Position (DSC)", value: "PDSC" },
    { name: "Age (ASC)", value: "AASC" },
    { name: "Age (DSC)", value: "ADSC" },
    { name: "State/Country (ASC)", value: "SASC" },
    { name: "State/Country (DSC)", value: "SDSC" },
    { name: "Height (ASC)", value: "HASC" },
    { name: "Height (DSC)", value: "HDSC" },
    { name: "Weight (ASC)", value: "WASC" },
    { name: "Weight (DSC)", value: "WDSC" },
    { name: "Wingspan (ASC)", value: "WSASC" },
    { name: "Wingspan (DSC)", value: "WSDSC" },
  ];

  // Get current page results
  const profilesToShow = getPaginatedResults();

  // Calculate display range
  const startItem = totalResults === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalResults);

  // Handle row click
  const handleRowClick = (item) => {
    if (!item.id) {
      console.error("Player profile item missing id", item);
      return;
    }
    
    if (isAdmin() || isSuperAdmin()) {
      window.open(`/player-profile/detail/${item.id}`, "_blank");
    } else {
      window.open(`/player-profile/preview/${item.id}`, "_blank");
    }
  };

  const filterSortPopupProps = {
    options: sortByElements,
    handleChange: (value) => setSortBy(value),
    currentFilter: sortBy,
    heightRange,
    handleHeightChange: (e, value) => setHeightRange(value),
    weightRange,
    handleWeightChange: (e, value) => setWeightRange(value),
    wingspanRange,
    handleWingspanChange: (e, value) => setWingspanRange(value),
    clearFilters: () => {
      setHeightRange(DEFAULT_HEIGHT);
      setWeightRange(DEFAULT_WEIGHT);
      setWingspanRange(DEFAULT_WINGSPAN);
    },
    cardClassName: styles.smartSearchFilterCard,
    popupClassName: styles.smartSearchSortPopup,
  };

  const desktopStatusPillClass = (item) => {
    const s = (item.Status || "").toLowerCase();
    const isTransferPortal =
      (item.Team || "").toLowerCase() === "transfer portal" ||
      s === "transfer portal";
    if (s === "committed" || s === "signed") {
      return styles.desktopStatusPillRed;
    }
    if (s === "undecided" || isTransferPortal) {
      return styles.desktopStatusPillGreen;
    }
    return styles.desktopStatusPillNeutral;
  };

  const renderDesktopPlayerCard = (item) => {
    const statusLine =
      item.Status === "Undecided" && item.committedTeam
        ? `${item.Status}: ${item.committedTeam}`
        : (item.Status === "Committed" || item.Status === "Signed") && item.committedTeam
          ? `${item.Status}: ${item.committedTeam}`
          : item.Status || "--";

    const projectionLine =
      item.pRange && item.Prlow !== "" && item.Prhigh !== ""
        ? item.Prlow === item.Prhigh
          ? item.Prhigh
          : `${item.Prlow} to ${item.Prhigh}`
        : "--";

    const hometownLine =
      item.hometown_City && !item.hometown_State_Country
        ? item.hometown_City
        : !item.hometown_City && item.hometown_State_Country
          ? item.hometown_State_Country
          : item.hometown_City && item.hometown_State_Country
            ? `${item.hometown_City}, ${item.hometown_State_Country}`
            : "--";

    const currentTeamDisplay = (() => {
      if (item.Team === "Transfer Portal" && (isAdmin() || isSuperAdmin())) {
        return item.Team;
      }
      if (item.Team !== "Transfer Portal") {
        return item.Team || "--";
      }
      return "--";
    })();

    const aauTeamWithAffiliation = (() => {
      const team = item.aauTeam && String(item.aauTeam).trim();
      const affil =
        item.aauAffil &&
        String(item.aauAffil).trim() &&
        item.aauAffil !== "None"
          ? String(item.aauAffil).trim()
          : "";
      if (!team) return "--";
      if (!affil) return team;
      return `${team} - ${affil}`;
    })();

    const rolesWithCount =
      item.roleSummaries && item.roleSummaries.roleCounts
        ? Object.entries(item.roleSummaries.roleCounts)
            .filter(([, roleData]) => roleData.finalCount > 0)
            .sort((a, b) => b[1].finalCount - a[1].finalCount)
        : [];

    const piScoreDisplay = (() => {
      if (!(isAdmin() || isSuperAdmin()) || !item.overallScore) {
        return "Coming Soon";
      }
      const n = parseFloat(item.overallScore);
      return Number.isNaN(n) ? "Coming Soon" : n.toFixed(1);
    })();

    return (
      <div
        key={item.id}
        className={styles.desktopPlayerCard}
        onClick={() => handleRowClick(item)}
        role="presentation"
      >
        <h3
          className={`${styles.desktopCardName} ${styles.desktopCardNameTopRow}`}
          title={`${item.FirstName || ""} ${item.LastName || ""}`.trim()}
        >
          {[item.FirstName, item.LastName].filter(Boolean).join(" ") || "--"}
        </h3>
        <div className={styles.desktopCardMain}>
          <div className={styles.desktopCardPhotoSlot}>
            <div className={styles.desktopCardPhotoFrame}>
              <Image
                className={styles.desktopCardPhotoImg}
                width={80}
                height={80}
                rounded
                src={item?.Headshot ? item.Headshot : previewImage}
                alt=""
                style={{
                  objectFit: item?.Headshot ? "cover" : "contain",
                  objectPosition: "center",
                }}
                onError={(e) => {
                  e.target.src = previewImage;
                  e.target.style.objectFit = "contain";
                }}
              />
            </div>
          </div>

          <div className={styles.desktopCardNameCol}>
            <div className={styles.desktopCardFieldStack}>
              <div className={styles.desktopCardFieldBlock}>
                <span className={styles.desktopMicroLabel}>Hometown</span>
                <p className={styles.desktopCardValueSmall}>{hometownLine}</p>
              </div>
              <div className={styles.desktopCardFieldBlock}>
                <span className={styles.desktopMicroLabel}>Nationality</span>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`nat-tip-${item.id}`}>
                      {countries.find((c) => c.value === item.Country)?.name || item.Country}
                    </Tooltip>
                  }
                >
                  <p className={styles.desktopCardValueSmall}>{item.Country || "--"}</p>
                </OverlayTrigger>
              </div>
            </div>
          </div>

          <div className={styles.desktopCardTeamCol}>
            <div className={styles.desktopCardFieldStack}>
              <div className={styles.desktopCardFieldBlock}>
                <span className={styles.desktopMicroLabel}>Current Team</span>
                <p className={styles.desktopCardValueSmall} title={String(currentTeamDisplay)}>
                  {currentTeamDisplay}
                </p>
              </div>
              <div className={styles.desktopCardFieldBlock}>
                <span className={styles.desktopMicroLabel}>AAU Team</span>
                <p
                  className={styles.desktopCardValueSmall}
                  title={aauTeamWithAffiliation !== "--" ? aauTeamWithAffiliation : undefined}
                >
                  {aauTeamWithAffiliation}
                </p>
              </div>
            </div>
          </div>

          <div className={styles.desktopCardRecruitCol}>
            <div className={styles.desktopCardFieldStack}>
              <div className={styles.desktopCardFieldBlock}>
                <span className={styles.desktopMicroLabel}>Recruitment</span>
                <div className={`${styles.desktopStatusPill} ${desktopStatusPillClass(item)}`}>
                  {statusLine}
                </div>
              </div>
              <div className={styles.desktopCardFieldBlock}>
                <span className={styles.desktopMicroLabel}>Projection</span>
                <p className={styles.desktopCardValueSmall}>{projectionLine}</p>
              </div>
            </div>
          </div>

          <div className={styles.desktopCardStatsCol}>
            <div className={styles.desktopStatGrid}>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>Height</span>
                <p className={styles.desktopStatValue}>{formatFeetInches(item.Height) || "--"}</p>
              </div>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>Weight</span>
                <p className={styles.desktopStatValue}>{formatWeightDisplay(item.Weight)}</p>
              </div>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>Wingspan</span>
                <p className={styles.desktopStatValue}>{formatFeetInches(item.Wingspan) || "--"}</p>
              </div>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>Hand</span>
                <p className={styles.desktopStatValue}>{item.Hand || "--"}</p>
              </div>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>Position</span>
                <p className={styles.desktopStatValue}>{item.Position ? item.Position : "--"}</p>
              </div>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>Year</span>
                <p className={styles.desktopStatValue}>{item.Year ? item.Year : "--"}</p>
              </div>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>DOB</span>
                <p
                  className={`${styles.desktopStatValue} ${styles.desktopStatValueDob}`}
                >
                  {item.DOB ? moment(item.DOB).format("l") : "--"}
                </p>
              </div>
              <div className={styles.desktopStatCell}>
                <span className={styles.desktopMicroLabel}>Age</span>
                <p className={styles.desktopStatValue}>
                {formatProfileAge(item.DOB, item.Age)}
                </p>
              </div>
            </div>
          </div>

          <div className={styles.desktopCardPiCol}>
            <div className={styles.desktopPiOuter}>
              <div className={styles.desktopPiBlock}>
                <span className={styles.desktopMicroLabel}>P.I. Score</span>
                <p
                  className={
                    piScoreDisplay === "Coming Soon"
                      ? `${styles.desktopPiValue} ${styles.desktopPiValueSoon}`
                      : styles.desktopPiValue
                  }
                >
                  {piScoreDisplay}
                </p>
              </div>
            </div>
          </div>
        </div>

        {rolesWithCount.length > 0 && (
          <div className={styles.desktopRolesRow}>
            <span className={styles.desktopRolesTitle}>Roles</span>
            <div className={styles.desktopRolesPills}>
              {rolesWithCount.map(([roleName, roleData]) => (
                <OverlayTrigger
                  key={roleName}
                  placement="top"
                  overlay={
                    <Tooltip id={`role-tip-d-${item.id}-${roleName}`}>
                      <div>
                        <strong>
                          {roleName} ({roleData.finalCount})
                        </strong>
                        {roleExplanations[roleName] && (
                          <div style={{ fontSize: "12px", marginTop: "4px" }}>
                            {roleExplanations[roleName]}
                          </div>
                        )}
                      </div>
                    </Tooltip>
                  }
                >
                  <span className={styles.rolePill}>
                    <span className={styles.rolePillIconWrap}>
                      {roleIcons[roleName]
                        ? renderRoleIcon(roleIcons[roleName], roleName, true, {
                            pill: true,
                            fillColor: "#fff",
                          })
                        : (
                            <span className={styles.rolePillFallbackLetter}>
                              {roleName.charAt(0)}
                            </span>
                          )}
                    </span>
                    <span>{roleName}</span>
                    <span className={styles.rolePillCount}>({roleData.finalCount})</span>
                  </span>
                </OverlayTrigger>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <Header search={false} />

      <Toast ref={toast} />
      <ConfirmDialog />

      <div className={styles.smartSearchShell}>
        <div className={styles.smartSearchInner}>
          <div className={styles.smartSearchTitle}>
            <h2>Smart Search</h2>
          </div>

          <div className={styles.smartSearchToolbar}>
            <div className={styles.smartSearchSearchBar}>
              <FaSearch className={styles.smartSearchSearchIcon} />
              <input
                type="text"
                placeholder="Quick Player Search"
                value={localSearch}
                onChange={(e) => {
                  setLocalSearch(e.target.value);
                  debouncedSearch(e.target.value);
                }}
              />
            </div>
            <div className={styles.smartSearchMeta}>
              Showing {startItem}-{endItem} of {totalResults} players
            </div>
            <Button
              label="Clear Filters"
              onClick={clearAllFilters}
              className={`p-button-outlined p-button-secondary ${styles.smartSearchClearBtn}`}
            />
          </div>

          <div className={styles.filtersContainer}>
            <div className={styles.filtersRowSmart}>
              {[
                { name: "team", label: "Team", options: teams, valueKey: "value", minWidth: "150px", isGrouped: true },
                { name: "country", label: "Nationality", options: countries, valueKey: "value", minWidth: "200px" },
                { name: "stateandcountry", label: "State/Country", options: stateandcountries, valueKey: "value", minWidth: "200px" },
                { name: "position", label: "Position", options: positions, valueKey: "value", minWidth: "200px" },
                { name: "year", label: "Year", options: years, valueKey: "value", minWidth: "120px" },
              ].map((el) => (
                <MultiSelect
                  key={el.name}
                  className={styles.smartSearchMultiselect}
                  maxSelectedLabels={3}
                  placeholder={el.label}
                  name={el.name}
                  value={filterBy[el.name]}
                  optionLabel="name"
                  optionValue={el.valueKey || "value"}
                  options={el.isGrouped ? el.options : el.options}
                  optionGroupLabel={el.isGrouped ? "label" : undefined}
                  optionGroupChildren={el.isGrouped ? "items" : undefined}
                  onChange={(e) => setFilterBy((prev) => ({ ...prev, [e.target.name]: e.value }))}
                  filter
                  showClear
                  display="chip"
                  style={{ width: "100%", minWidth: 0 }}
                />
              ))}
              {!isMobile && (
                <div className={styles.intlOnlyBox}>
                  <input
                    type="checkbox"
                    id="intlPlayerFilter"
                    checked={filterBy.intlPlayer || false}
                    onChange={(e) =>
                      setFilterBy((prev) => ({ ...prev, intlPlayer: e.target.checked }))
                    }
                  />
                  <label htmlFor="intlPlayerFilter">INTL Only</label>
                </div>
              )}
            </div>

            <div className={styles.filtersRowSmart}>
              {[
                { name: "aauTeam", label: "AAU Team", options: aauTeams, valueKey: "value", isGrouped: true },
                { name: "affliation", label: "Affiliation", options: affliations, valueKey: "value" },
                { name: "status", label: "Status", options: statuses, valueKey: "value" },
                { name: "roles", label: "Roles", options: roles, valueKey: "name" },
              ].map((el) => (
                <MultiSelect
                  key={el.name}
                  className={styles.smartSearchMultiselect}
                  maxSelectedLabels={3}
                  placeholder={el.label}
                  name={el.name}
                  value={filterBy[el.name]}
                  optionLabel="name"
                  optionValue={el.valueKey || "value"}
                  options={el.isGrouped ? el.options : el.options}
                  optionGroupLabel={el.isGrouped ? "label" : undefined}
                  optionGroupChildren={el.isGrouped ? "items" : undefined}
                  onChange={(e) => setFilterBy((prev) => ({ ...prev, [e.target.name]: e.value }))}
                  filter
                  showClear
                  display="chip"
                  style={{ width: "100%", minWidth: 0 }}
                />
              ))}
              {isMobile && (
                <div className={styles.intlOnlyBox}>
                  <input
                    type="checkbox"
                    id="intlPlayerFilter"
                    checked={filterBy.intlPlayer || false}
                    onChange={(e) =>
                      setFilterBy((prev) => ({ ...prev, intlPlayer: e.target.checked }))
                    }
                  />
                  <label htmlFor="intlPlayerFilter">INTL Only</label>
                </div>
              )}
              <div className={styles.sortFilterSlot}>
                <FiltersComponent {...filterSortPopupProps} />
              </div>
            </div>
          </div>

      <div className={styles.smartSearchResultsWrap}>
        {isLoading ? (
          <div className="d-flex justify-content-center my-5">
            <ProgressSpinner />
          </div>
        ) : !isMobile ? (
          <div className={styles.smartSearchDesktopList}>
            {profilesToShow.length === 0 ? (
              <p className="text-center py-5">
                No players found matching your criteria or{" "}
                <span
                  className={styles.requestPlayerLink}
                  onClick={openRequestPlayerModal}
                >
                  Can&apos;t Find a Player?{" "}
                  <span className={styles.requestPlayerUnderline}>
                    Request a Player
                  </span>
                </span>
              </p>
            ) : (
              profilesToShow.map((item) => renderDesktopPlayerCard(item))
            )}
          </div>
        ) : (
          <div className={styles.mobileCardsContainer}>
            {profilesToShow.length === 0 ? (
              <p className="text-center py-5">
                No players found matching your criteria or{" "}
                <span
                  className={styles.requestPlayerLink}
                  onClick={openRequestPlayerModal}
                >
                  Can&apos;t Find a Player?{" "}
                  <span className={styles.requestPlayerUnderline}>
                    Request a Player
                  </span>
                </span>
              </p>
            ) : (
              profilesToShow.map((item) => (
                <MobilePlayerCard key={item.id} item={item} />
              ))
            )}
          </div>
        )}
      </div>
      
      {totalResults > 0 && (
        <div className={styles.smartSearchPagination}>
          <div className={styles.smartSearchPaginationSummary}>
            Showing {startItem}-{endItem} of {totalResults} players
          </div>
          
          <Pagination
            current={currentPage}
            total={totalResults}
            pageSize={pageSize}
            onChange={(page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            }}
            onShowSizeChange={(current, size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            showSizeChanger
            pageSizeOptions={['10', '25', '50', '100']}
          />
        </div>
      )}
        </div>
      </div>

      <Modal
        show={showRequestPlayerModal}
        onHide={closeRequestPlayerModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Want to see a player in our database?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-4">
            Give us the player&apos;s name, we&apos;ll get our team on it.
          </p>
          <form onSubmit={handleRequestPlayerSubmit}>
            <h6 className="mb-2">
              Requested by <span style={{ color: "red" }}>*</span>
            </h6>
            <Row className="mb-3">
              <Col md={4} className="mb-2">
                <input
                  type="text"
                  name="requesterFirstName"
                  className="form-control"
                  placeholder="First Name"
                  value={requestPlayerForm.requesterFirstName}
                  onChange={handleRequestPlayerChange}
                />
                {requestPlayerErrors.requesterFirstName && (
                  <small className="text-danger">
                    {requestPlayerErrors.requesterFirstName}
                  </small>
                )}
              </Col>
              <Col md={4} className="mb-2">
                <input
                  type="text"
                  name="requesterLastName"
                  className="form-control"
                  placeholder="Last Name"
                  value={requestPlayerForm.requesterLastName}
                  onChange={handleRequestPlayerChange}
                />
                {requestPlayerErrors.requesterLastName && (
                  <small className="text-danger">
                    {requestPlayerErrors.requesterLastName}
                  </small>
                )}
              </Col>
              <Col md={4} className="mb-2">
                <input
                  type="email"
                  name="requesterEmail"
                  className="form-control"
                  placeholder="Email"
                  value={requestPlayerForm.requesterEmail}
                  onChange={handleRequestPlayerChange}
                />
                {requestPlayerErrors.requesterEmail && (
                  <small className="text-danger">
                    {requestPlayerErrors.requesterEmail}
                  </small>
                )}
              </Col>
            </Row>

            <h6 className="mb-2">
              Player <span style={{ color: "red" }}>*</span>
            </h6>
            <Row className="mb-3">
              <Col md={4} className="mb-2">
                <input
                  type="text"
                  name="playerFirstName"
                  className="form-control"
                  placeholder="First Name"
                  value={requestPlayerForm.playerFirstName}
                  onChange={handleRequestPlayerChange}
                />
                {requestPlayerErrors.playerFirstName && (
                  <small className="text-danger">
                    {requestPlayerErrors.playerFirstName}
                  </small>
                )}
              </Col>
              <Col md={4} className="mb-2">
                <input
                  type="text"
                  name="playerLastName"
                  className="form-control"
                  placeholder="Last Name"
                  value={requestPlayerForm.playerLastName}
                  onChange={handleRequestPlayerChange}
                />
                {requestPlayerErrors.playerLastName && (
                  <small className="text-danger">
                    {requestPlayerErrors.playerLastName}
                  </small>
                )}
              </Col>
              <Col md={4} className="mb-2">
                <input
                  type="text"
                  name="playerTeam"
                  className="form-control"
                  placeholder="Team"
                  value={requestPlayerForm.playerTeam}
                  onChange={handleRequestPlayerChange}
                />
                {requestPlayerErrors.playerTeam && (
                  <small className="text-danger">
                    {requestPlayerErrors.playerTeam}
                  </small>
                )}
              </Col>
            </Row>

            <div className="mb-3">
              <label className="mb-1">Notes</label>
              <textarea
                name="notes"
                className="form-control"
                rows={4}
                placeholder="Notes"
                value={requestPlayerForm.notes}
                onChange={handleRequestPlayerChange}
              />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={closeRequestPlayerModal}
                disabled={isSubmittingRequestPlayer}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-dark"
                disabled={isSubmittingRequestPlayer}
              >
                {isSubmittingRequestPlayer ? "Submitting..." : "Request Player"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};